import { useState } from 'react';
import { api, ApiError, formatDate, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { Page, RollMeResponse, RollSyncResponse, VoterRecord, VoterStatus } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge } from '../../ui/components';

const OFFICER_ROLES = ['REGISTRATION_OFFICER', 'ELECTORAL_COMMISSIONER'];
const STATUSES: VoterStatus[] = ['PENDING_CONFIRMATION', 'ACTIVE', 'INACTIVE', 'REMOVED_DECEASED', 'REMOVED_INELIGIBLE', 'REMOVED_OTHER'];

/** Voter roll — GET /roll/me self-check (any T2+ NIN) plus GET /roll officer console. */
export function VoterRoll() {
  const { persona } = useAuth();
  const isOfficer = persona?.roles.some((r) => OFFICER_ROLES.includes(r)) ?? false;

  return (
    <div>
      <PageHeader title="Voter Roll" subtitle="Electoral roll integrity — auto-maintained from M01, with officer oversight." />
      <SelfCheckPanel />
      <div className="mt-8">
        <h2 className="mb-3 text-sm font-bold text-slate-700">Officer console — all voter records</h2>
        {!isOfficer ? <RoleNotice roles={OFFICER_ROLES} /> : <OfficerConsole />}
      </div>
    </div>
  );
}

function SelfCheckPanel() {
  const { persona } = useAuth();
  const meQ = useApiQuery<RollMeResponse>(persona ? '/api/v1/m39/roll/me' : null);
  const [syncBusy, setSyncBusy] = useState(false);
  const [syncResult, setSyncResult] = useState<RollSyncResponse | null>(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sync = async () => {
    if (!persona?.nin) return;
    setSyncBusy(true);
    setError(null);
    try {
      const res = await api.post<RollSyncResponse>(`/api/v1/m39/roll/sync/${encodeURIComponent(persona.nin)}`);
      setSyncResult(res);
      meQ.reload();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Sync failed');
    } finally {
      setSyncBusy(false);
    }
  };

  const confirmReassignment = async () => {
    setConfirmBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m39/roll/me/confirm-reassignment');
      meQ.reload();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Confirmation failed');
    } finally {
      setConfirmBusy(false);
    }
  };

  if (!persona?.nin) {
    return (
      <div className="card p-5">
        <h2 className="mb-1 text-sm font-bold text-slate-700">Am I registered?</h2>
        <p className="text-xs text-slate-500">This persona has no verified NIN on its token, so a self roll-check isn&rsquo;t available.</p>
      </div>
    );
  }

  return (
    <div className="card p-5">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-bold text-slate-700">Am I registered? — {persona.nin}</h2>
        <button className="btn-secondary" onClick={sync} disabled={syncBusy}>
          {syncBusy ? 'Syncing…' : 'Sync against M01'}
        </button>
      </div>
      {error ? <ErrorNote message={error} /> : null}
      {syncResult ? (
        <div className="mb-3 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-800">
          Sync outcome: <span className="font-semibold">{syncResult.outcome.replaceAll('_', ' ')}</span>
        </div>
      ) : null}
      {meQ.loading ? (
        <Spinner />
      ) : meQ.error ? (
        <ErrorNote message={meQ.error} />
      ) : !meQ.data?.registered ? (
        <EmptyState title="Not registered on the electoral roll" hint="Run a sync above, or file a NEW_REGISTRATION claim during an open roll revision." />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <div className="field-label">Status</div>
            <StatusBadge status={meQ.data.voterRecord!.status} />
          </div>
          <div>
            <div className="field-label">Electoral district</div>
            {meQ.data.electoralDistrict?.name ?? '—'}
          </div>
          <div>
            <div className="field-label">Polling station</div>
            {meQ.data.pollingStation?.name ?? 'Not yet assigned'}
          </div>
          <div>
            <div className="field-label">Last synced with M01</div>
            {formatDateTime(meQ.data.voterRecord!.lastSyncedWithM01At)}
          </div>
          {meQ.data.pendingReassignment ? (
            <div className="col-span-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
              <div className="text-xs font-semibold text-amber-800">
                Pending address-change re-assignment: {meQ.data.pendingReassignment.electoralDistrict?.name ?? '—'} —{' '}
                {meQ.data.pendingReassignment.pollingStation?.name ?? 'station TBD'}
              </div>
              <div className="mt-0.5 text-xs text-amber-700">{meQ.data.pendingReassignment.reason}</div>
              <button className="btn-primary mt-2 !px-3 !py-1.5 text-xs" onClick={confirmReassignment} disabled={confirmBusy}>
                {confirmBusy ? 'Confirming…' : 'Confirm re-assignment'}
              </button>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

function OfficerConsole() {
  const [status, setStatus] = useState<VoterStatus | ''>('');
  const [q, setQ] = useState('');
  const [syncNin, setSyncNin] = useState('');
  const [syncBusy, setSyncBusy] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [syncResult, setSyncResult] = useState<RollSyncResponse | null>(null);
  const [selected, setSelected] = useState<VoterRecord | null>(null);

  const params = new URLSearchParams({ limit: '50' });
  if (status) params.set('status', status);
  if (q.trim()) params.set('q', q.trim());
  const listQ = useApiQuery<Page<VoterRecord>>(`/api/v1/m39/roll?${params.toString()}`);

  const runSync = async () => {
    setSyncBusy(true);
    setSyncError(null);
    try {
      const res = await api.post<RollSyncResponse>(`/api/v1/m39/roll/sync/${encodeURIComponent(syncNin)}`);
      setSyncResult(res);
      listQ.reload();
    } catch (e) {
      setSyncError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Sync failed');
    } finally {
      setSyncBusy(false);
    }
  };

  return (
    <div>
      <div className="card mb-4 flex flex-wrap items-end gap-2 p-4">
        <Field label="Sync a NIN against M01 (18th-birthday add / address-change / refresh)" className="min-w-[260px] flex-1">
          <input className="field-input" value={syncNin} onChange={(e) => setSyncNin(e.target.value)} placeholder="e.g. 108-1001-1-1-85" />
        </Field>
        <button className="btn-primary" onClick={runSync} disabled={syncBusy || !syncNin.trim()}>
          {syncBusy ? 'Syncing…' : 'Sync'}
        </button>
      </div>
      {syncError ? (
        <div className="mb-3">
          <ErrorNote message={syncError} />
        </div>
      ) : null}
      {syncResult ? (
        <div className="mb-3 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-800">
          Outcome: <span className="font-semibold">{syncResult.outcome.replaceAll('_', ' ')}</span>
        </div>
      ) : null}

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Field label="NIN or name contains" className="min-w-[220px] flex-1">
          <input className="field-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. Confait" />
        </Field>
        <Field label="Status" className="!mb-0">
          <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value as VoterStatus | '')}>
            <option value="">All</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
        </Field>
      </div>

      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No voter records match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">NIN</th>
                <th className="table-head">Full name</th>
                <th className="table-head">DOB</th>
                <th className="table-head">Source</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((v) => (
                <tr key={v.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelected(v)}>
                  <td className="table-cell font-mono text-xs">{v.nin}</td>
                  <td className="table-cell font-medium">{v.fullName}</td>
                  <td className="table-cell text-xs">{formatDate(v.dob)}</td>
                  <td className="table-cell text-xs">{v.source.replaceAll('_', ' ')}</td>
                  <td className="table-cell">
                    <StatusBadge status={v.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {selected ? <VoterRecordModal record={selected} onClose={() => setSelected(null)} /> : null}
    </div>
  );
}

function VoterRecordModal({ record, onClose }: { record: VoterRecord; onClose: () => void }) {
  return (
    <Modal title={record.fullName} onClose={onClose}>
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div>
          <div className="field-label">NIN</div>
          <span className="font-mono text-xs">{record.nin}</span>
        </div>
        <div>
          <div className="field-label">Status</div>
          <StatusBadge status={record.status} />
        </div>
        <div>
          <div className="field-label">Date of birth</div>
          {formatDate(record.dob)}
        </div>
        <div>
          <div className="field-label">Source</div>
          {record.source.replaceAll('_', ' ')}
        </div>
        <div className="col-span-2">
          <div className="field-label">Registered address</div>
          {record.registeredAddressLine} ({record.registeredAddressDistrict})
        </div>
        <div>
          <div className="field-label">Electoral district id</div>
          <span className="font-mono text-xs">{record.electoralDistrictId}</span>
        </div>
        <div>
          <div className="field-label">Polling station id</div>
          <span className="font-mono text-xs">{record.pollingStationId ?? '—'}</span>
        </div>
        {record.removedAt ? (
          <div className="col-span-2 rounded-lg border border-rose-200 bg-rose-50 p-2.5 text-xs text-rose-700">
            Removed {formatDateTime(record.removedAt)}: {record.removedReason}
          </div>
        ) : null}
        <div>
          <div className="field-label">Last synced with M01</div>
          {formatDateTime(record.lastSyncedWithM01At)}
        </div>
      </div>
    </Modal>
  );
}
